import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { CATEGORIES, UNITS } from "../lib/constants";
import { unitCosts, servingCost } from "../lib/calc";
import { useAuth } from "../context/AuthContext";
import { unitCost, money } from "../lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { ChevronDown, Loader2 } from "lucide-react";

const empty = {
  name: "", brand: "", category: "Other", price: "", package_qty: "",
  package_unit: "oz", serving_amount: "", serving_unit: "oz",
  servings_stated: "", notes: "", store: "", date: "",
};

export default function FoodDialog({ open, onOpenChange, food, onSaved }) {
  const { user } = useAuth();
  const cur = user?.currency || "USD";
  const [f, setF] = useState(empty);
  const [more, setMore] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (food) {
        const ap = food.metrics || {};
        setF({
          name: food.name || "", brand: food.brand || "", category: food.category || "Other",
          price: ap.price ?? "", package_qty: ap.package_qty ?? "",
          package_unit: food.package_unit || "oz",
          serving_amount: food.serving_amount ?? "", serving_unit: food.serving_unit || "oz",
          servings_stated: ap.servings_on_package ?? "", notes: food.notes || "",
          store: ap.store || "", date: ap.date || "",
        });
        setMore(true);
      } else {
        setF(empty);
        setMore(false);
      }
    }
  }, [open, food]);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const costs = unitCosts(f.price, f.package_qty, f.package_unit, f.servings_stated);
  const sc = servingCost(f.serving_amount, f.serving_unit, f.price, f.package_qty, f.package_unit, f.servings_stated);

  const save = async () => {
    if (!f.name || !f.price || !f.package_qty) {
      toast.error("Name, price and package amount are required");
      return;
    }
    setSaving(true);
    const payload = {
      name: f.name, brand: f.brand, category: f.category,
      package_unit: f.package_unit,
      serving_amount: f.serving_amount ? parseFloat(f.serving_amount) : null,
      serving_unit: f.serving_unit,
      servings_stated: f.servings_stated ? parseFloat(f.servings_stated) : null,
      notes: f.notes, store: f.store, date: f.date || null,
      price: parseFloat(f.price), package_qty: parseFloat(f.package_qty),
      needs_package_size: false,
    };
    try {
      if (food) await api.put(`/foods/${food.id}`, payload);
      else await api.post("/foods", payload);
      toast.success(food ? "Food updated" : "Food added");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[480px]" data-testid="food-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{food ? "Edit Food" : "Add Food"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Item name *</Label>
            <Input data-testid="food-name-input" value={f.name} onChange={(e) => set("name")(e.target.value)}
              placeholder="e.g. Atlantic Salmon" className="mt-1.5" autoFocus />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Price *</Label>
              <Input data-testid="food-price-input" type="number" step="0.01" value={f.price}
                onChange={(e) => set("price")(e.target.value)} placeholder="0.00" className="mt-1.5" />
            </div>
            <div>
              <Label>Amount *</Label>
              <Input data-testid="food-qty-input" type="number" step="0.01" value={f.package_qty}
                onChange={(e) => set("package_qty")(e.target.value)} placeholder="0" className="mt-1.5" />
            </div>
            <div>
              <Label>Unit *</Label>
              <Select value={f.package_unit} onValueChange={set("package_unit")}>
                <SelectTrigger data-testid="food-unit-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* live preview */}
          {(costs.cost_per_oz || costs.cost_per_ml || costs.cost_per_item || costs.cost_per_gram) && (
            <div className="rounded-xl bg-brand-light p-3 text-sm" data-testid="food-cost-preview">
              <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-brand">
                {costs.cost_per_oz != null && <span>{unitCost(costs.cost_per_oz, cur)}/oz</span>}
                {costs.cost_per_gram != null && <span>{unitCost(costs.cost_per_gram, cur, 4)}/g</span>}
                {costs.cost_per_ml != null && <span>{unitCost(costs.cost_per_ml, cur, 4)}/ml</span>}
                {costs.cost_per_fl_oz != null && <span>{unitCost(costs.cost_per_fl_oz, cur)}/fl oz</span>}
                {costs.cost_per_item != null && <span>{unitCost(costs.cost_per_item, cur)}/item</span>}
                {costs.cost_per_mfg_serving != null && <span>{unitCost(costs.cost_per_mfg_serving, cur)}/mfg serving</span>}
              </div>
              {sc != null && (
                <div className="mt-1.5 font-semibold text-slate-700">
                  Your serving ({f.serving_amount} {f.serving_unit}) = <span className="text-brand">{money(sc, cur)}</span>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>My serving amount</Label>
              <Input data-testid="food-serving-amount-input" type="number" step="0.01" value={f.serving_amount}
                onChange={(e) => set("serving_amount")(e.target.value)} placeholder="7" className="mt-1.5" />
            </div>
            <div>
              <Label>Serving unit</Label>
              <Select value={f.serving_unit} onValueChange={set("serving_unit")}>
                <SelectTrigger data-testid="food-serving-unit-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <button onClick={() => setMore(!more)} data-testid="food-more-toggle"
            className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
            <ChevronDown className={`h-4 w-4 transition-transform ${more ? "rotate-180" : ""}`} />
            {more ? "Fewer details" : "More details (optional)"}
          </button>

          {more && (
            <div className="space-y-4 border-t border-slate-100 pt-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Brand</Label>
                  <Input value={f.brand} onChange={(e) => set("brand")(e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={f.category} onValueChange={set("category")}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Servings on package</Label>
                  <Input type="number" step="0.01" value={f.servings_stated}
                    onChange={(e) => set("servings_stated")(e.target.value)} className="mt-1.5" placeholder="optional" />
                </div>
                <div>
                  <Label>Store</Label>
                  <Input value={f.store} onChange={(e) => set("store")(e.target.value)} className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label>Date purchased</Label>
                <Input type="date" value={f.date} onChange={(e) => set("date")(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={f.notes} onChange={(e) => set("notes")(e.target.value)} className="mt-1.5" rows={2} />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button onClick={save} disabled={saving} data-testid="food-save-button"
            className="w-full rounded-full bg-brand py-6 text-base font-semibold hover:bg-brand-hover">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : food ? "Save changes" : "Add to Library"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
